import React, { useState, useRef, useEffect } from 'react';
import './changeLanguage.css';
import CurrencyFlag from 'react-currency-flags';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

const languages = [
    { code: 'en', name: 'English', flag: 'GBP', shortName: 'EN' },
    { code: 'ge', name: 'Georgian', flag: 'GEL', shortName: 'GE' },
    { code: 'ru', name: 'Russian', flag: 'RUB', shortName: 'RU' },
    { code: 'tr', name: 'Turkish', flag: 'TRY', shortName: 'TR' },
];

const ChangeLanguage = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolling, setScrolling] = useState(false);
    const dropdownRef = useRef(null);
    const { i18n } = useTranslation();

    // Initialize language from localStorage or default to English
    const getInitialLanguage = () => {
        const savedLanguageCode = localStorage.getItem('selectedLanguage');
        const savedLanguage = languages.find(lang => lang.code === savedLanguageCode);
        return savedLanguage || languages[0];
    };

    const [selectedLanguage, setSelectedLanguage] = useState(getInitialLanguage);

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    const selectLanguage = (language) => {
        setSelectedLanguage(language);
        i18n.changeLanguage(language.code);
        // Save to localStorage
        localStorage.setItem('selectedLanguage', language.code);
        setIsOpen(false);
    };

    const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setIsOpen(false);
        }
    };

    // Restore saved language on component mount
    useEffect(() => {
        const savedLanguageCode = localStorage.getItem('selectedLanguage');
        if (savedLanguageCode) {
            i18n.changeLanguage(savedLanguageCode);
        }
    }, [i18n]);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setScrolling(window.scrollY > 20);
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Set initial language based on i18n current language
    useEffect(() => {
        const currentLang = languages.find(lang => lang.code === i18n.language);
        if (currentLang) {
            setSelectedLanguage(currentLang);
        }
    }, [i18n.language]);

    return (
        <div className="language-selector" ref={dropdownRef}>
            <button 
                className={`language-button ${scrolling ? 'scrolled' : ''} ${isOpen ? 'active' : ''}`} 
                onClick={toggleDropdown}
                aria-label="Change language"
                type="button"
            >
                <div className="button-content">
                    <div className="flag-wrapper">
                        <CurrencyFlag
                            className="currency-flag-main"
                            currency={selectedLanguage.flag}
                        />
                    </div>
                    <span className="lang-code">{selectedLanguage.shortName}</span>
                    <ChevronDown className={`chevron-icon ${isOpen ? 'rotate' : ''}`} size={14} />
                </div>
            </button>
            
            {isOpen && (
                <div className={`dropdown ${scrolling ? 'dropdown-scrolled' : ''}`}>
                    <div className="dropdown-inner">
                        {languages.map((language) => (
                            <button
                                key={language.code}
                                className={`dropdown-item ${selectedLanguage.code === language.code ? 'active' : ''}`}
                                onClick={() => selectLanguage(language)}
                                type="button"
                            >
                                <div className="flag-wrapper-dropdown">
                                    <CurrencyFlag 
                                        className="currency-flag" 
                                        currency={language.flag} 
                                    />
                                </div>
                                <span className="language-name">{language.name}</span>
                                {selectedLanguage.code === language.code && (
                                    <span className="checkmark">✓</span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChangeLanguage;